import supabase from '../supabase';

async function getUserType() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Check if user is a company using proper query format
    const { data: company } = await supabase
      .from('company_profiles')
      .select('id')
      .match({ id: user.id })
      .maybeSingle();

    return company ? 'company' : 'user';
  } catch (error) {
    console.error('Error detecting user type:', error);
    return 'user'; // Default to user type if check fails
  }
}

// En services/savedItems.js
export const getSavedItems = async (itemType) => {
  try {
    // Necesitamos obtener el usuario actual
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error('No se encontró usuario autenticado');

    // Detectar tipo de usuario
    const userType = await getUserType();
    
    const { data: savedItems, error } = await supabase
      .from('saved_items')
      .select('*')
      .eq('saver_id', user.id)
      .eq('saver_type', userType) // ✅ Usar el tipo correcto
      .eq('item_type', itemType);
    
    if (error) throw error;
    
    // Si necesitas detalles de los cursos, haz una consulta adicional
    if (itemType === 'course' && savedItems.length > 0) {
      const courseIds = savedItems.map(item => item.item_id);
      
      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('*')
        .in('id', courseIds);
      
      if (coursesError) throw coursesError;
      
      // Combina los datos de saved_items con los datos de courses
      return savedItems.map(item => {
        const courseDetails = courses.find(course => course.id === item.item_id);
        return {
          ...item,
          details: courseDetails || null
        };
      });
    }
    
    return savedItems;
  } catch (err) {
    console.error('Error in getSavedItems:', err);
    throw err;
  }
};

// Save an item - ✅ CORREGIDO
const saveItem = async (itemType, itemId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    // ✅ Detectar tipo de usuario correctamente
    const userType = await getUserType();

    // ✅ Verificar primero si ya existe
    const { data: existing } = await supabase
      .from('saved_items')
      .select('id')
      .eq('saver_id', user.id)
      .eq('saver_type', userType)
      .eq('item_type', itemType)
      .eq('item_id', itemId)
      .single();

    if (existing) {
      return { success: true, message: 'Elemento ya estaba guardado', id: existing.id };
    }

    const { data, error } = await supabase
      .from('saved_items')
      .insert({
        saver_id: user.id,
        saver_type: userType, // ✅ Usar tipo correcto, no hardcodear 'user'
        item_type: itemType,
        item_id: itemId
      })
      .select()
      .single();

    if (error) {
      // ✅ Manejar duplicados
      if (error.code === '23505') {
        return { success: true, message: 'Elemento ya estaba guardado' };
      }
      throw error;
    }

    return { success: true, message: 'Elemento guardado exitosamente', id: data.id };
  } catch (error) {
    console.error('Error saving item:', error);
    throw error;
  }
};

// Remove a saved item - ✅ CORREGIDO
const unsaveItem = async (itemType, itemId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    // ✅ Detectar tipo de usuario
    const userType = await getUserType();

    const { error } = await supabase
      .from('saved_items')
      .delete()
      .match({
        saver_id: user.id,
        saver_type: userType, // ✅ Usar tipo correcto
        item_type: itemType,
        item_id: itemId
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error unsaving item:', error);
    throw error;
  }
};

// Check if item is saved - ✅ CORREGIDO
const checkIfSaved = async (itemType, itemId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // ✅ Detectar tipo de usuario
    const userType = await getUserType();

    const { data, error } = await supabase
      .from('saved_items')
      .select('id')
      .match({
        saver_id: user.id,
        saver_type: userType, // ✅ Usar tipo correcto
        item_type: itemType,
        item_id: itemId
      })
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking saved status:', error);
    return false;
  }
};

// Get IDs of saved items - ✅ CORREGIDO
const getSavedItemIds = async (itemType) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // ✅ Detectar tipo de usuario
    const userType = await getUserType();

    const { data, error } = await supabase
      .from('saved_items')
      .select('item_type, item_id')
      .eq('saver_id', user.id)
      .eq('saver_type', userType) // ✅ Usar tipo correcto
      .eq('item_type', itemType);

    if (error) {
      console.error('Error fetching saved item IDs:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getSavedItemIds:', error);
    return [];
  }
};

const fetchSavedItem = async (userId, itemType, itemId) => {
  const { data, error } = await supabase
    .from('saved_items')
    .select('id')
    .eq('saver_id', userId) // ✅ Cambiado de user_id a saver_id
    .eq('item_type', itemType)
    .eq('item_id', itemId)
    .single();

  if (error) {
    console.error('Error:', error);
    return null;
  }
  
  return data;
};

export const removeSavedItem = async (itemType, itemId) => {
  try {
    const response = await fetch(`/api/saved-items/${itemType}/${itemId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to remove saved ${itemType}`);
    }

    return true;
  } catch (error) {
    console.error(`Error removing saved ${itemType}:`, error);
    throw error;
  }
};

// Export all functions
export {
  saveItem,
  unsaveItem,
  checkIfSaved,
  getSavedItemIds
};